import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  Space,
  Drawer,
  Row,
  Col,
  Input,
  Select,
  DatePicker,
  Progress,
  Badge,
  Form,
} from "antd";
import type { TableColumnsType } from "antd";
import {
  FilterOutlined,
  CalendarOutlined,
  DollarOutlined,
  UserOutlined,
  CheckCircleOutlined,
  EyeOutlined,
  SearchOutlined,
  ReloadOutlined,
  SaveOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Icon, TooltipComponent } from "../../components/Component";
import TransactionFlowDefaultTemplate from "../others/transactionFlowDefaultTemplate";
import RABillAside from "./raBillAside";
import { formatCurrency } from "../../utils/Utils";
import { mockRABills } from "./mockRABills";
import { useSelector } from "react-redux";
import dropdownApis from "../../api/WorkOrderManagement/DropdownApis";

const { Option } = Select;
const { RangePicker } = DatePicker;

// Filter State Interface
interface FilterState {
  siteId?: string[];
  contractorName?: string[];
  workOrderId?: string[];
}

const ViewRABillsApproved: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get flowType from URL
  const searchParams = new URLSearchParams(location.search);
  const flowType = searchParams.get("flowType");
  const isClientFlow = flowType?.toLowerCase() === "client";

  const [sm, setSm] = useState(false);
  const [approvedRABills, setApprovedRABills] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [filters, setFilters] = useState<FilterState>({});
  const [tempFilters, setTempFilters] = useState<FilterState>({});
  const [savedFilters, setSavedFilters] = useState<{
    [key: string]: FilterState;
  }>({});
  const [filterForm] = Form.useForm();
  const [contractorOptions, setContractorOptions] = useState<any[]>([]);
  const [siteOptions, setSiteOptions] = useState<any[]>([]);
  const [workOrderOptions, setWorkOrderOptions] = useState<any[]>([]);
  const { data: currentUser } = useSelector((state: any) => state.currentUser);

  useEffect(() => {
    loadApprovedRABills();
  }, []);

  // Fetch site options from currentUser
  useEffect(() => {
    if (currentUser) {
      const { siteAccessibility } = currentUser;
      if (siteAccessibility) {
        const _siteOptions: any = [];
        siteAccessibility?.forEach((item: any) => {
          _siteOptions.push({ label: item.siteName, value: item.siteID });
        });
        setSiteOptions([..._siteOptions]);
      }
    }
  }, [currentUser]);

  // Count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        if (Array.isArray(value) && value.length > 0) count++;
        else if (typeof value === "string" && value.trim() !== "") count++;
      }
    });
    return count;
  };

  // Filter Functions
  const handleFilterDrawerOpen = async () => {
    setTempFilters({ ...filters });
    filterForm.setFieldsValue(filters);
    setFilterDrawerVisible(true);

    // Fetch dropdown data when drawer opens
    try {
      const flowTypeParam = isClientFlow ? "Client" : "Contractor";

      // Fetch contractors
      const partyType = isClientFlow ? "Client" : "Contractor";
      const contractorsResponse = await dropdownApis.fetchContractorDropDown(
        partyType
      );
      if (
        contractorsResponse &&
        contractorsResponse.status &&
        contractorsResponse.data
      ) {
        const contractorOptions = contractorsResponse.data.map((item: any) => ({
          label: item.companyName || "N/A",
          value: item.contractorID?.toString(),
        }));
        setContractorOptions(contractorOptions);
      }

      // Fetch work orders
      const workOrdersResponse = await dropdownApis.fetchWorkOrderDropdown(
        flowTypeParam
      );
      if (
        workOrdersResponse &&
        workOrdersResponse.status &&
        workOrdersResponse.data
      ) {
        const workOrderOptions = workOrdersResponse.data.map((item: any) => {
          const label =
            item.workOrderNumber ||
            item.workOrderSequenceId ||
            item.sequenceId ||
            item.workOrderTitle ||
            `WO-${item.workOrderId || item.id || "N/A"}`;
          const value = item.workOrderId?.toString() || item.id?.toString();
          return { label, value };
        });
        setWorkOrderOptions(workOrderOptions);
      }
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const handleFilterDrawerClose = () => {
    setFilterDrawerVisible(false);
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleApplyFilters = () => {
    filterForm
      .validateFields()
      .then((values) => {
        const processedFilters: FilterState = {
          siteId:
            values.siteId && values.siteId.length > 0
              ? values.siteId
              : undefined,
          contractorName:
            values.contractorName && values.contractorName.length > 0
              ? values.contractorName
              : undefined,
          workOrderId:
            values.workOrderId && values.workOrderId.length > 0
              ? values.workOrderId
              : undefined,
        };

        // Remove undefined values
        Object.keys(processedFilters).forEach((key) => {
          if (processedFilters[key as keyof FilterState] === undefined) {
            delete processedFilters[key as keyof FilterState];
          }
        });

        // Always create a new object reference to trigger useEffect
        setFilters({ ...processedFilters });
        setFilterDrawerVisible(false);
      })
      .catch((error) => {
        console.error("Form validation failed:", error);
      });
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    filterForm.resetFields();
  };

  const handleSaveFilter = () => {
    const filterName = prompt("Enter a name for this filter:");
    if (filterName && filterName.trim()) {
      setSavedFilters((prev) => ({
        ...prev,
        [filterName.trim()]: { ...filters },
      }));
    }
  };

  const handleLoadFilter = (filterName: string) => {
    const savedFilter = savedFilters[filterName];
    if (savedFilter) {
      setFilters(savedFilter);
      setTempFilters(savedFilter);
      filterForm.setFieldsValue(savedFilter);
    }
  };

  const loadApprovedRABills = () => {
    setLoading(true);
    try {
      // Filter RA Bills that are approved or higher (Posted, Paid)
      const approvedBills = mockRABills.filter((bill) =>
        ["Approved", "Posted", "Paid"].includes(bill.status)
      );
      setApprovedRABills(approvedBills);
    } catch (error) {
      console.error("Error loading approved RA Bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const getPaymentStatusColor = (status: string): string => {
    const statusColors: { [key: string]: string } = {
      Paid: "success",
      "Partially Paid": "warning",
      Unpaid: "error",
    };
    return statusColors[status] || "default";
  };

  const getPaymentProgress = (
    paidAmount: number,
    totalAmount: number
  ): number => {
    // Handle null, undefined, or NaN values
    const safePaidAmount = Number(paidAmount) || 0;
    const safeTotalAmount = Number(totalAmount) || 0;

    if (safeTotalAmount === 0) return 0;

    const percentage = Math.round((safePaidAmount / safeTotalAmount) * 100);

    // Ensure the result is a valid number
    return isNaN(percentage) ? 0 : percentage;
  };

  const handleViewRABill = (record: any) => {
    const searchParams = new URLSearchParams(location.search);
    const flowType = searchParams.get("flowType");
    const flowTypeQuery = flowType ? `&flowType=${flowType}` : "";
    navigate(
      `${process.env.PUBLIC_URL}/ra-bills-view-details/${record.raBillId}?mode=view${flowTypeQuery}`
    );
  };

  // KPI calculations
  const totalApproved = approvedRABills.length;
  const totalValue = approvedRABills.reduce(
    (sum, bill) => sum + (Number(bill.currentBillValue) || 0),
    0
  );
  const totalPaid = approvedRABills.reduce(
    (sum, bill) => sum + (Number(bill.paidAmount) || 0),
    0
  );
  const pendingPayment = totalValue - totalPaid;

  const columns: TableColumnsType<any> = [
    {
      title: "RA Bill #",
      dataIndex: "raBillSequenceId",
      key: "raBillSequenceId",
      width: 220,
      fixed: "left",
      render: (text: string, record: any) => (
        <span
          className="inlineTableCellLink"
          id={`raBillView-${record?.raBillId}`}
          onClick={() => handleViewRABill(record)}
        >
          {text}
        </span>
      ),
    },
    {
      title: "WO #",
      dataIndex: "workOrderNumber",
      key: "workOrderNumber",
      width: 220,
      // render: (text, record) => (
      //   <div>
      //     <div className="fw-bold text-primary">{text}</div>
      //     <div className="text-muted small mt-1">Section: {record.section}</div>
      //   </div>
      // ),
    },
    {
      title: "Title",
      dataIndex: "workOrderTitle",
      key: "workOrderTitle",
      width: 250,
      render: (text: string, record: any, index: any) => {
        return (
          <>
            <span id={`title-tooltip-${index}`}>
              {text?.length > 30 ? `${text?.substring(0, 30)}... ` : text}
            </span>
            {text?.length > 30 && (
              <TooltipComponent
                iconClass="info-circle text-info"
                icon="info"
                id={`title-tooltip-${index}`}
                direction="top"
                text={text}
                containerClassName={""}
              />
            )}
          </>
        );
      },
    },
    {
      title: "Site/Project",
      key: "siteProject",
      width: 200,
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.siteName}</div>
          <div className="text-muted small">{record.projectName}</div>
        </div>
      ),
    },
    {
      title: isClientFlow ? "Client" : "Contractor",
      dataIndex: "contractorName",
      key: "contractorName",
      width: 180,
    },
    {
      title: "RA Period",
      key: "raPeriod",
      width: 250,
      render: (_, record) => (
        <div>
          <div className="">
            {dayjs(record.raPeriodFrom).format("DD-MMM-YYYY")}{" "}
            <span className="">-</span>{" "}
            {dayjs(record.raPeriodTo).format("DD-MMM-YYYY")}
          </div>
        </div>
      ),
    },
    {
      title: "RA Bill Value",
      dataIndex: "netPayable",
      key: "netPayable",
      width: 150,
      align: "right",
      render: (value) => (
        <div className="fw-bold">{formatCurrency(value, "INR")}</div>
      ),
      sorter: (a, b) => a.netPayable - b.netPayable,
    },
    {
      title: "Paid Amount",
      dataIndex: "paidAmount",
      key: "paidAmount",
      width: 140,
      align: "right",
      render: (paidAmount, record) => (
        <div>
          <div className="fw-bold text-success">
            {formatCurrency(paidAmount, "INR")}
          </div>
          <div className="text-muted small">
            {(() => {
              const percentage = getPaymentProgress(
                paidAmount,
                record.netPayable
              );
              return `${percentage}% paid`;
            })()}
          </div>
        </div>
      ),
    },
    {
      title: "Balance Amount",
      dataIndex: "balanceAmount",
      key: "balanceAmount",
      width: 140,
      align: "right",
      render: (balance) => (
        <div
          className="fw-bold"
          style={{ color: balance > 0 ? "#ff4d4f" : "#52c41a" }}
        >
          {formatCurrency(balance, "INR")}
        </div>
      ),
    },
    {
      title: "Payment Progress",
      key: "progress",
      width: 150,
      align: "center",
      render: (_, record) => {
        const progress = getPaymentProgress(
          record.paidAmount,
          record.netPayable
        );
        const color =
          progress === 100 ? "#52c41a" : progress >= 50 ? "#faad14" : "#ff4d4f";
        return (
          <Progress
            percent={progress}
            size="small"
            strokeColor={color}
            showInfo={true}
          />
        );
      },
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      width: 130,
      render: (status) => (
        <Tag color={getPaymentStatusColor(status)}>{status}</Tag>
      ),
    },
    {
      title: "Approved Date",
      dataIndex: "approvedAt",
      key: "approvedAt",
      width: 130,
      render: (date) => (date ? dayjs(date).format("DD-MMM-YYYY") : "---"),
    },
    {
      title: "Last Payment",
      key: "lastContactDate",
      width: 140,
      render: (_, record) => (
        <div>
          {record.lastContactDate ? (
            <>
              <div className="fw-bold">
                {dayjs(record.lastContactDate).format("DD-MMM-YY")}
              </div>
              <div className="text-muted small">{record.paymentMode}</div>
            </>
          ) : (
            <span className="text-muted">No Payment</span>
          )}
        </div>
      ),
    },
    // {
    //   title: "Actions",
    //   key: "actions",
    //   fixed: "right",
    //   width: 150,
    //   render: (_, record) => (
    //     <Space wrap>
    //       <Button
    //         type="primary"
    //         size="small"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewRABill(record)}
    //       >
    //         View Details
    //       </Button>
    //     </Space>
    //   ),
    // },
  ];

  return (
    <>
      <TransactionFlowDefaultTemplate
        moduleKey="ra-bills-view-approved"
        title="View RA Bills (Approved)"
        subtitle="Comprehensive view of all approved Running Account Bills with payment tracking"
        columns={columns}
        dataSource={approvedRABills}
        loading={loading}
        customFilters={filters}
        sideNavigation={
          <div
            className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg ${
              sm ? "content-active" : ""
            }`}
          >
            <RABillAside updateSm={setSm} sm={sm} />
          </div>
        }
        topRightJSX={
          <Space>
            <Badge count={getActiveFilterCount()} size="small">
              <Button
                type="primary"
                icon={<FilterOutlined />}
                onClick={handleFilterDrawerOpen}
              >
                Advanced Filters
              </Button>
            </Badge>
          </Space>
        }
        extra={{
          tableScrollX: 1900,
          showSearch: true,
          excelExport: true,
          searchPlaceholder: "Search by RA Bill#, WO#, or Contractor...",
          kpiWidgets: [
            {
              title: "Total Approved Bills",
              value: totalApproved,
              icon: <CheckCircleOutlined />,
              color: "#52c41a",
            },
            {
              title: "Total Bill Value",
              value: formatCurrency(totalValue, "INR"),
              icon: <DollarOutlined />,
              color: "#1890ff",
            },
            {
              title: "Amount Paid",
              value: formatCurrency(totalPaid, "INR"),
              icon: <CalendarOutlined />,
              color: "#52c41a",
            },
            {
              title: "Pending Payment",
              value: formatCurrency(pendingPayment, "INR"),
              icon: <UserOutlined />,
              color: "#ff4d4f",
            },
          ],
        }}
      />

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        width={420}
        open={filterDrawerVisible}
        onClose={handleFilterDrawerClose}
        extra={
          <Space size="middle">
            <Badge count={getActiveFilterCount()} size="small">
              <Button size="small" type="text" icon={<FilterOutlined />} />
            </Badge>
            <Button
              size="small"
              onClick={handleResetFilters}
              icon={<ReloadOutlined />}
            >
              Reset
            </Button>
          </Space>
        }
        footer={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button
                size="small"
                onClick={handleSaveFilter}
                icon={<SaveOutlined />}
              >
                Save Filter
              </Button>
              {Object.keys(savedFilters).length > 0 && (
                <Select
                  placeholder="Load saved filter"
                  size="small"
                  style={{ width: 150 }}
                  suffixIcon={<FolderOpenOutlined />}
                  onChange={handleLoadFilter}
                  allowClear
                >
                  {Object.keys(savedFilters).map((filterName) => (
                    <Option key={filterName} value={filterName}>
                      {filterName}
                    </Option>
                  ))}
                </Select>
              )}
            </Space>
            <Space>
              <Button onClick={handleFilterDrawerClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<SearchOutlined />}
              >
                Apply Filters
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={filterForm} layout="vertical" initialValues={filters}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="siteId" label="Site/Project">
                <Select
                  mode="multiple"
                  placeholder="Select Sites"
                  options={siteOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name="contractorName"
                label={isClientFlow ? "Client" : "Contractor"}
              >
                <Select
                  mode="multiple"
                  placeholder={
                    isClientFlow ? "Select Clients" : "Select Contractors"
                  }
                  options={contractorOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item name="workOrderId" label="Work Order">
                <Select
                  mode="multiple"
                  placeholder="Select Work Orders"
                  options={workOrderOptions}
                  allowClear
                  showSearch
                  filterOption={(input, option: any) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    </>
  );
};

export default ViewRABillsApproved;
